import {
  AdminFormGrid,
  Button,
  Card,
  CardHeader,
  FormField,
  InlineAlert,
  PhoneInput,
  Select,
  TextInput,
} from "@constructorfleet/ui/design-system";
import { useEffect, useMemo, useState } from "react";
import type { UaProductDbClient } from "../pages";
import type { UaCallSettings } from "../ua-call-settings";
import { loadUaCallSettings, saveUaCallSettings } from "../ua-call-settings";

function serialize(value: unknown) {
  return JSON.stringify(value);
}

function validateSettings(settings: UaCallSettings) {
  const errors: Record<string, string> = {};
  if (settings.maxActiveCallsPerDialFromNumber < 1) {
    errors.maxActiveCalls = "Must be at least 1.";
  }
  if (settings.maxCallDurationSeconds < 1) {
    errors.maxDuration = "Must be at least 1 second.";
  }
  if (settings.webhookStatusCallbackBaseUrl.trim().length > 0) {
    try {
      new URL(settings.webhookStatusCallbackBaseUrl);
    } catch {
      errors.webhookUrl = "Must be a valid URL.";
    }
  }
  return errors;
}

export function RuntimeSettingsForm({ dbClient }: { dbClient: UaProductDbClient }) {
  const [settings, setSettings] = useState<UaCallSettings | null>(null);
  const [initial, setInitial] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const requestAdmin = dbClient.requestAdmin;

  useEffect(() => {
    let mounted = true;
    if (!requestAdmin) {
      setLoading(false);
      return;
    }
    void (async () => {
      const result = await loadUaCallSettings(requestAdmin);
      if (!mounted) return;
      if (result.ok) {
        setSettings(result.settings);
        setInitial(serialize(result.settings));
      } else {
        setError(result.error);
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [requestAdmin]);

  const isDirty = useMemo(() => {
    if (!settings || !initial) return false;
    return serialize(settings) !== initial;
  }, [settings, initial]);

  if (!requestAdmin) {
    return (
      <InlineAlert tone="info">Runtime settings are not available in this context.</InlineAlert>
    );
  }

  if (loading) {
    return <InlineAlert tone="info">Loading runtime settings…</InlineAlert>;
  }

  if (error) {
    return <InlineAlert tone="danger">{error}</InlineAlert>;
  }

  if (!settings) return null;

  const validationErrors = validateSettings(settings);
  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <Card wide>
      <CardHeader title="Runtime Settings" meta={isDirty ? "Unsaved changes" : "In sync"} />
      <form
        className="card-body ds-stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (hasErrors || !requestAdmin) return;
          setSaveMessage(null);
          void (async () => {
            const result = await saveUaCallSettings(settings, requestAdmin);
            if (result.ok) {
              setInitial(serialize(settings));
              setSaveMessage("Runtime settings saved.");
            } else {
              setSaveMessage(result.error);
            }
          })();
        }}
      >
        {saveMessage ? (
          <InlineAlert tone={saveMessage.includes("saved") ? "success" : "danger"}>
            {saveMessage}
          </InlineAlert>
        ) : null}

        <div className="admin-form">
          <h4>Call Routing</h4>
          <p className="hc-desc">
            Set the numbers and callback endpoint used when the platform places outbound calls.
          </p>
          <FormField
            label="Dial from numbers"
            htmlFor="ua-admin-dial-from-phone-0"
            helpText="US numbers formatted as full +1 numbers."
          >
            <div className="ds-stack">
              {settings.dialFromPhoneNumbers.map((value, index) => (
                <div key={`dial-from-${index}`} className="shell-actions">
                  <PhoneInput
                    id={`ua-admin-dial-from-phone-${index}`}
                    value={value}
                    valueFormat="e164"
                    onChange={(nextValue) => {
                      setSettings((current) =>
                        current
                          ? {
                              ...current,
                              dialFromPhoneNumbers: current.dialFromPhoneNumbers.map((item, i) =>
                                i === index ? nextValue : item
                              ),
                            }
                          : current
                      );
                    }}
                    required
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={settings.dialFromPhoneNumbers.length <= 1}
                    onClick={() => {
                      setSettings((current) =>
                        current
                          ? {
                              ...current,
                              dialFromPhoneNumbers: current.dialFromPhoneNumbers.filter(
                                (_, i) => i !== index
                              ),
                            }
                          : current
                      );
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="shell-actions">
                <Button
                  type="button"
                  size="sm"
                  variant="brand"
                  onClick={() => {
                    setSettings((current) =>
                      current
                        ? {
                            ...current,
                            dialFromPhoneNumbers: [...current.dialFromPhoneNumbers, ""],
                          }
                        : current
                    );
                  }}
                >
                  Add dial-from number
                </Button>
              </div>
            </div>
          </FormField>
          <AdminFormGrid>
            <FormField label="Dial to number" htmlFor="ua-admin-dial-to-phone">
              <PhoneInput
                id="ua-admin-dial-to-phone"
                value={settings.dialToPhoneNumber}
                valueFormat="e164"
                onChange={(nextValue) => {
                  setSettings((current) =>
                    current ? { ...current, dialToPhoneNumber: nextValue } : current
                  );
                }}
                required
              />
            </FormField>
            <FormField
              label="Webhook callback URL"
              htmlFor="ua-admin-webhook-url"
              errorText={validationErrors.webhookUrl}
            >
              <TextInput
                id="ua-admin-webhook-url"
                type="url"
                value={settings.webhookStatusCallbackBaseUrl}
                onChange={(event) => {
                  const nextValue = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current ? { ...current, webhookStatusCallbackBaseUrl: nextValue } : current
                  );
                }}
                required
              />
            </FormField>
          </AdminFormGrid>
        </div>

        <div className="admin-form">
          <h4>Safety Limits</h4>
          <p className="hc-desc">
            Cap concurrency and call duration so one bad run cannot monopolize the outbound pool.
          </p>
          <AdminFormGrid>
            <FormField
              label="Max active calls per phone number"
              htmlFor="ua-admin-max-active-calls"
              errorText={validationErrors.maxActiveCalls}
            >
              <TextInput
                id="ua-admin-max-active-calls"
                type="number"
                min={1}
                value={String(settings.maxActiveCallsPerDialFromNumber)}
                onChange={(event) => {
                  const raw = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current
                      ? { ...current, maxActiveCallsPerDialFromNumber: Number(raw || 1) }
                      : current
                  );
                }}
                required
              />
            </FormField>
            <FormField
              label="Max duration (sec)"
              htmlFor="ua-admin-max-duration"
              errorText={validationErrors.maxDuration}
            >
              <TextInput
                id="ua-admin-max-duration"
                type="number"
                min={1}
                value={String(settings.maxCallDurationSeconds)}
                onChange={(event) => {
                  const raw = (event.target as HTMLInputElement)?.value ?? "";
                  setSettings((current) =>
                    current ? { ...current, maxCallDurationSeconds: Number(raw || 1) } : current
                  );
                }}
                required
              />
            </FormField>
          </AdminFormGrid>
        </div>

        <div className="admin-form">
          <h4>Provider Credentials</h4>
          <p className="hc-desc">Keep only the active provider credentials current.</p>
          <AdminFormGrid>
            <FormField label="Voice provider" htmlFor="ua-admin-voice-provider">
              <Select
                id="ua-admin-voice-provider"
                value={settings.voiceProvider}
                onChange={(event) => {
                  const nextValue =
                    ((event.target as HTMLSelectElement)?.value as "twilio" | "telnyx") ?? "twilio";
                  setSettings((current) =>
                    current ? { ...current, voiceProvider: nextValue } : current
                  );
                }}
              >
                <option value="twilio">Twilio</option>
                <option value="telnyx">Telnyx</option>
              </Select>
            </FormField>
            {settings.voiceProvider === "twilio" ? (
              <>
                <FormField label="Twilio Account SID" htmlFor="ua-admin-twilio-sid">
                  <TextInput
                    id="ua-admin-twilio-sid"
                    value={settings.twilioAccountSid}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, twilioAccountSid: value } : current
                      );
                    }}
                  />
                </FormField>
                <FormField label="Twilio Auth Token" htmlFor="ua-admin-twilio-token">
                  <TextInput
                    id="ua-admin-twilio-token"
                    type="password"
                    value={settings.twilioAuthToken}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, twilioAuthToken: value } : current
                      );
                    }}
                  />
                </FormField>
              </>
            ) : (
              <>
                <FormField label="Telnyx API Key" htmlFor="ua-admin-telnyx-key">
                  <TextInput
                    id="ua-admin-telnyx-key"
                    type="password"
                    value={settings.telnyxApiKey}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, telnyxApiKey: value } : current
                      );
                    }}
                  />
                </FormField>
                <FormField label="Telnyx Public Key" htmlFor="ua-admin-telnyx-pub">
                  <TextInput
                    id="ua-admin-telnyx-pub"
                    value={settings.telnyxPublicKey}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, telnyxPublicKey: value } : current
                      );
                    }}
                  />
                </FormField>
                <FormField label="Telnyx Connection ID" htmlFor="ua-admin-telnyx-conn">
                  <TextInput
                    id="ua-admin-telnyx-conn"
                    value={settings.telnyxConnectionId}
                    onChange={(event) => {
                      const value = (event.target as HTMLInputElement)?.value ?? "";
                      setSettings((current) =>
                        current ? { ...current, telnyxConnectionId: value } : current
                      );
                    }}
                  />
                </FormField>
              </>
            )}
          </AdminFormGrid>
        </div>

        <div className="shell-actions">
          <Button type="submit" disabled={!isDirty || hasErrors}>
            Save runtime settings
          </Button>
        </div>
      </form>
    </Card>
  );
}
